import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth } from "../config/firebase";
import axios from "axios";
import { clearAllCache } from "../utils/persistentCache";

const AuthContext = createContext();

// 会话：自上次访问本站起超过此时长需重新登录（滑动窗口，有访问则顺延）
const SESSION_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000; // 7 天
const LAST_ACTIVITY_KEY = "lastActivityTime";

// 合并并发的 token 刷新请求，避免多个 401 同时触发多次 getIdToken(true)
let refreshTokenPromise = null;

const FATAL_AUTH_ERROR_CODES = new Set([
  "auth/user-disabled",
  "auth/user-token-expired",
  "auth/id-token-revoked",
  "auth/invalid-user-token",
  "auth/requires-recent-login",
]);

const refreshIdToken = (user) => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = user.getIdToken(true).finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

const isFatalAuthError = (error) => {
  if (error?.code && FATAL_AUTH_ERROR_CODES.has(error.code)) {
    return true;
  }
  return false;
};

// 更新最后活动时间（任意访问/操作本站时调用，用于顺延登录有效期）
export const updateLastActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

// 检查自上次记录的活动时间起是否已超过会话时长（未访问本站满 7 天）
export const isSessionExpired = () => {
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!lastActivity) {
    return false;
  }
  const lastActivityTime = parseInt(lastActivity, 10);
  if (isNaN(lastActivityTime)) {
    return false;
  }
  const now = Date.now();
  return now - lastActivityTime > SESSION_TIMEOUT_MS;
};

// 清除活动时间记录
export const clearLastActivity = () => {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth 必须在 AuthProvider 内使用");
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  // 从 localStorage 恢复角色，如果没有则默认为 null
  const [userRole, setUserRole] = useState(() => {
    const savedRole = localStorage.getItem("userRole");
    return savedRole !== null ? parseInt(savedRole, 10) : null;
  });
  const [canManageAdsCampaign, setCanManageAdsCampaign] = useState(false);
  const [loading, setLoading] = useState(true);

  // 检查会话并登出
  const checkSessionAndLogout = useCallback(async () => {
    if (isSessionExpired()) {
      clearLastActivity();
      await signOut(auth);
      delete axios.defaults.headers.common["Authorization"];
      setCurrentUser(null);
      localStorage.removeItem("userRole");
      return true;
    }
    return false;
  }, []);

  // 仅在 token 确实失效或会话过期时强制登出
  const forceLogout = useCallback(async () => {
    clearLastActivity();
    localStorage.removeItem("userRole");
    delete axios.defaults.headers.common["Authorization"];
    try {
      await signOut(auth);
    } catch (e) {
      console.error("强制登出失败:", e);
    }
    setCurrentUser(null);
    setUserRole(null);
    setCanManageAdsCampaign(false);
  }, []);

  // 获取用户角色
  const fetchUserRole = useCallback(async () => {
    try {
      const { getApiUrl } = await import("../config/api");
      const { getCachedUserInfo, setCachedUserInfo } = await import("../utils/persistentCache");
      
      // 尝试从 localStorage 加载缓存
      const cachedUserInfo = getCachedUserInfo();
      
      if (cachedUserInfo && cachedUserInfo.role !== undefined) {
        console.log('[用户信息] 使用 localStorage 缓存');
        const role = cachedUserInfo.role;
        setUserRole(role);
        setCanManageAdsCampaign(role === 0 || cachedUserInfo.canManageAdsCampaign === true);
        localStorage.setItem("userRole", role.toString());
        console.log("用户角色已从缓存获取:", role === 0 ? "管理员" : "普通用户");
        return;
      }
      
      // 缓存不存在，从 API 获取
      console.log('[用户信息] 从 API 获取数据...');
      const response = await axios.get(getApiUrl('/api/user'), {
        timeout: 10000,
      });
      const userInfo = response.data;
      const role = userInfo?.role !== undefined ? userInfo.role : 1;

      setUserRole(role);
      setCanManageAdsCampaign(userInfo?.canManageAdsCampaign === true);
      localStorage.setItem("userRole", role.toString());

      // 缓存用户信息
      setCachedUserInfo(userInfo);
      console.log('[用户信息] 数据已缓存到 localStorage');
      console.log("用户角色已获取:", role === 0 ? "管理员" : "普通用户");
    } catch (error) {
      console.error("获取用户角色失败:", error);
      // 如果获取失败，尝试从 localStorage 恢复
      const savedRole = localStorage.getItem("userRole");
      if (savedRole !== null) {
        setUserRole(parseInt(savedRole, 10));
      } else {
        // 如果都没有，默认为普通用户
        setUserRole(1);
        setCanManageAdsCampaign(false);
        localStorage.setItem("userRole", "1");
      }
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // 必须先根据「上次活动时间」判断是否超过 7 天未访问，再更新活动时间，否则会永远判不过期
        if (isSessionExpired()) {
          try {
            await signOut(auth);
          } catch (e) {
            console.error("会话过期登出失败:", e);
          }
          delete axios.defaults.headers.common["Authorization"];
          clearLastActivity();
          localStorage.removeItem("userRole");
          setCurrentUser(null);
          setUserRole(null);
          setCanManageAdsCampaign(false);
          setLoading(false);
          window.location.href = "/login";
          return;
        }

        setCurrentUser(user);

        try {
          const token = await user.getIdToken();
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        } catch (e) {
          console.error("初始化 token 失败（保留登录态，后续请求会重试）:", e);
        }

        setLoading(false);

        const savedRole = localStorage.getItem("userRole");
        if (savedRole !== null) {
          setUserRole(parseInt(savedRole, 10));
        }

        fetchUserRole().catch((error) => {
          console.error("获取用户角色失败:", error);
        });

        updateLastActivity();
      } else {
        delete axios.defaults.headers.common["Authorization"];
        setCurrentUser(null);
        setUserRole(null);
        setCanManageAdsCampaign(false);
        clearLastActivity();
        localStorage.removeItem("userRole");
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [checkSessionAndLogout, fetchUserRole]);

  // 监听用户活动，更新最后活动时间
  useEffect(() => {
    if (!currentUser) return;

    // 更新活动时间的函数
    const handleActivity = () => {
      updateLastActivity();
    };

    // 监听各种用户活动事件
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // 定期检查会话（每 5 分钟；超过 7 天未访问则登出）
    const checkInterval = setInterval(async () => {
      const expired = await checkSessionAndLogout();
      if (expired) {
        window.location.href = "/login";
      }
    }, 5 * 60 * 1000);

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      clearInterval(checkInterval);
    };
  }, [currentUser, checkSessionAndLogout]);

  // 配置axios拦截器，在每次请求时更新活动时间和自动刷新token
  useEffect(() => {
    if (!currentUser) return;

    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        try {
          const token = await currentUser.getIdToken(false);
          config.headers["Authorization"] = `Bearer ${token}`;
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          updateLastActivity();
        } catch (error) {
          console.error("获取 token 失败:", error);
          if (
            !config.headers["Authorization"] &&
            axios.defaults.headers.common["Authorization"]
          ) {
            config.headers["Authorization"] =
              axios.defaults.headers.common["Authorization"];
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        updateLastActivity();
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            if (currentUser) {
              const newToken = await refreshIdToken(currentUser);
              axios.defaults.headers.common["Authorization"] =
                `Bearer ${newToken}`;
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              updateLastActivity();
              return axios(originalRequest);
            }
          } catch (refreshError) {
            console.error("Token 刷新失败:", refreshError);
            const sessionExpired = await checkSessionAndLogout();
            if (sessionExpired || isFatalAuthError(refreshError)) {
              await forceLogout();
              window.location.href = "/login";
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [currentUser, checkSessionAndLogout, forceLogout]);

  const login = useCallback(async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const token = await userCredential.user.getIdToken();
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    // 登录成功后更新活动时间
    updateLastActivity();
    return userCredential;
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    delete axios.defaults.headers.common["Authorization"];
    clearLastActivity();
    clearAllCache(); // 清除所有 localStorage 缓存
    console.log('[缓存] 登出时清除所有缓存');
  }, []);

  const value = useMemo(
    () => ({
      currentUser,
      userRole, // 0=管理员, 1=普通用户
      canManageAdsCampaign, // 是否可操作广告（创建/开启/暂停），管理员或已分配权限
      login,
      logout,
      loading,
      updateLastActivity,
      checkSessionAndLogout,
    }),
    [
      currentUser,
      userRole,
      canManageAdsCampaign,
      login,
      logout,
      loading,
      checkSessionAndLogout,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
